import {
    ActionType,
    getDailyCommentsAction,
    getMonthlyCommentsAction,
    getDailyEstimatedBehaviorAction,
    initMonthlyDataAction,
    getEstimatedBehaviorHistoriesAction,
    setPeriodAction,
    getEstimatedBehaviorAveragesAction,
    getEstimatedBehaviorAlertAction,
    getEstimatedBehaviorAlertPeriodAction,
    getLastMonthEstimatedBehaviorHistoriesAction,
    resetDailyCommentsAction,
    getCareRecipientLatestInfoAction
} from "./actions";
import { DailyEstimatedBehaviorsJson } from "../../../domain/estimated_behavior/DailyEstimatedBehaviors";
import { MonthlyCommentsJson } from "../../../domain/report/monthly/MonthlyComments";
import { MonthlyAverageDataJson } from "../../../domain/report/monthly/MonthlyAverageData";
import { createSlice } from "@reduxjs/toolkit";
import { EstimatedBehaviorHistoriesJson } from "../../../domain/estimated_behavior/EstimatedBehaviorHistories";
import { DateObject } from "../../../domain/fundamental/DateObject";
import { Average } from "../../../domain/report/average/Average";
import { DailyAlertCommentListJson } from "../../../domain/report/daily/DailyAlertCommentList";
import { CareRecipientLatestInformationJson } from "../../../domain/report/CareRecipientLatestInformation";

export type DashBoardGraphCategory = "SLEEP" | "TOILET" | "BATH" | "GOING_OUT"

export type ReportState = Readonly<{
    dailyAlertCommentList: DailyAlertCommentListJson | null,
    monthlyComments: MonthlyCommentsJson | null,
    monthlyData: MonthlyAverageDataJson[],
    dailyEstimatedBehaviors: DailyEstimatedBehaviorsJson | null,
    careRecipientLatestInfo: CareRecipientLatestInformationJson[] | null,
    estimatedBehaviorHistories: EstimatedBehaviorHistoriesJson,
    lastMonthEstimatedBehaviorHistories: EstimatedBehaviorHistoriesJson,
    periodStartDate: string,
    periodEndDate: string
    averages: Average,
    alerts: {
        careRecipientId: string,
        category: string,
        message: string,
        date: string
    }[] | null,
    alertsByCategory: {
        [category in DashBoardGraphCategory]: {
            [date: string]: string[]
        }
    } | null
}>;

const reportInitialState: ReportState = {
    dailyAlertCommentList: null,
    monthlyComments: null,
    monthlyData: [],
    dailyEstimatedBehaviors: null,
    careRecipientLatestInfo: null,
    estimatedBehaviorHistories: {
        toilet: [],
        goingOut: [],
        bath: [],
        sleep: [],
        meal: []
    },
    lastMonthEstimatedBehaviorHistories: {
        toilet: [],
        goingOut: [],
        bath: [],
        sleep: [],
        meal: []
    },
    periodStartDate: DateObject.today().addDay(-31).format(),
    periodEndDate: DateObject.today().addDay(-2).format(),
    averages: {
        sleep: null,
        bath: null,
        goingOut: null,
        toilet: null,
        meal: null,
        laundry: null,
        sweeping: null,
        tvWatch: null,
    },
    alerts: null,
    alertsByCategory: null
};

const slice = createSlice({
    name: ActionType.base,
    initialState: reportInitialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(
                initMonthlyDataAction,
                (state) => {
                    state.monthlyData = [];
                }
            )
            .addCase(
                getDailyCommentsAction.pending,
                () => {}
            )
            .addCase(
                getDailyCommentsAction.fulfilled,
                (state, action) => {
                    const { date, dailyAlertCommentList } = action.payload;
                    state.dailyAlertCommentList = {
                        ...state.dailyAlertCommentList,
                        [date]: dailyAlertCommentList
                    };
                }
            )
            .addCase(
                getDailyCommentsAction.rejected,
                () => {}
            )
            .addCase(
                resetDailyCommentsAction,
                (state) => {
                    state.dailyAlertCommentList = null;
                }
            )
            .addCase(
                getMonthlyCommentsAction.pending,
                () => {}
            )
            .addCase(
                getMonthlyCommentsAction.fulfilled,
                (state, action) => {
                    const { payload : { monthlyComments } } = action;
                    state.monthlyComments = monthlyComments;
                    state.monthlyData = createAverageDataList(monthlyComments);
                }
            )
            .addCase(
                getMonthlyCommentsAction.rejected,
                () => {}
            )
            .addCase(
                getDailyEstimatedBehaviorAction.pending,
                () => {}
            )
            .addCase(
                getDailyEstimatedBehaviorAction.fulfilled,
                (state, action) => {
                    const { payload } = action;
                    state.dailyEstimatedBehaviors = payload;
                }
            )
            .addCase(
                getDailyEstimatedBehaviorAction.rejected,
                () => {}
            )
            .addCase(
                getCareRecipientLatestInfoAction.pending,
                () => {}
            )
            .addCase(
                getCareRecipientLatestInfoAction.fulfilled,
                (state, action) => {
                    const { payload } = action;
                    state.careRecipientLatestInfo = payload.informationList;
                }
            )
            .addCase(
                getCareRecipientLatestInfoAction.rejected,
                (state) => {
                    state.careRecipientLatestInfo = [];
                }
            )
            .addCase(
                getEstimatedBehaviorHistoriesAction.pending,
                () => {}
            )
            .addCase(
                getEstimatedBehaviorHistoriesAction.fulfilled,
                (state, action) => {
                    const { payload } = action;
                    state.estimatedBehaviorHistories = payload.histories;
                }
            )
            .addCase(
                getEstimatedBehaviorHistoriesAction.rejected,
                () => {}
            )
            .addCase(
                getLastMonthEstimatedBehaviorHistoriesAction.pending,
                () => {}
            )
            .addCase(
                getLastMonthEstimatedBehaviorHistoriesAction.fulfilled,
                (state, action) => {
                    const { payload } = action;
                    state.lastMonthEstimatedBehaviorHistories = payload.histories;
                }
            )
            .addCase(
                getLastMonthEstimatedBehaviorHistoriesAction.rejected,
                () => {}
            )
            .addCase(
                setPeriodAction,
                (state, action) => {
                    state.periodStartDate = action.payload.periodStartDate;
                    state.periodEndDate = action.payload.periodEndDate;
                }
            )
            .addCase(
                getEstimatedBehaviorAveragesAction.fulfilled,
                (state, action) => {
                    const { payload } = action;
                    const { sleep, bath, goingOut, toilet, meal, laundry, tvWatch, sweeping } = payload ;
                    state.averages.sleep = sleep;
                    state.averages.bath = bath;
                    state.averages.goingOut = goingOut;
                    state.averages.toilet = toilet;
                    state.averages.meal = meal;
                    state.averages.laundry = laundry;
                    state.averages.tvWatch = tvWatch;
                    state.averages.sweeping = sweeping;
                }
            ).addCase(
                getEstimatedBehaviorAlertAction.fulfilled,
                (state, action) => {
                    const { payload } = action;
                    const { alerts } = payload;
                    state.alerts = alerts;
                }
            ).addCase(
                getEstimatedBehaviorAlertAction.rejected,
                () => {}
            ).addCase(
                getEstimatedBehaviorAlertAction.pending,
                () => {}
            ).addCase(
                getEstimatedBehaviorAlertPeriodAction.fulfilled,
                (state, action) => {
                    const { payload } = action;
                    const { alerts } = payload;
                    state.alertsByCategory = alerts;
                }
            );
    }
});

const createAverageDataList = (monthlyComments: MonthlyCommentsJson) => {
    const { medicalComments, statisticalComments } = monthlyComments;

    const averageDataListFromMedicalComments = medicalComments.map(comment => ({
        category: comment.category,
        data: comment.averageData
    }));

    const averageDataListFromStatisticalComments = statisticalComments.map(comment => ({
        category: comment.category,
        data: comment.averageData
    }));

    return [...averageDataListFromMedicalComments, ...averageDataListFromStatisticalComments];
};

export const reportReducer = slice.reducer;
